// Robust, multi-strategy element locator for the Lyric Tester record/replay
// engine. The goal is to survive re-renders and dynamic ids by capturing SEVERAL
// hints per element and resolving them back in priority order (mirrors
// Playwright / testing-library best practice):
//   data-testid/-test/-cy  ->  aria-label  ->  id  ->  name(+type)
//   ->  visible text (buttons/links/options)  ->  scoped CSS path (nth-of-type)
//
// Everything is framework-agnostic and takes an explicit `win`/`doc` so it runs
// both inside a deployed app's window AND under jsdom in tests.

function getAttr(el, name) {
    return el && el.getAttribute ? el.getAttribute(name) : null;
}

function cssEscapeIdent(win, s) {
    if (win && win.CSS && typeof win.CSS.escape === 'function') return win.CSS.escape(s);
    // Minimal fallback: escape anything that isn't a safe identifier char.
    return String(s).replace(/[^a-zA-Z0-9_-]/g, (ch) => '\\' + ch);
}

// Escape a value for use inside an ="..." attribute selector.
function attrValue(s) {
    return String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function normText(el) {
    const t = (el && el.textContent ? el.textContent : '').replace(/\s+/g, ' ').trim();
    return t.length > 80 ? t.slice(0, 80) : t;
}

function nthOfType(el) {
    let i = 1;
    let sib = el;
    while ((sib = sib.previousElementSibling)) {
        if (sib.tagName === el.tagName) i++;
    }
    return i;
}

// Build a stable-ish CSS path from the nearest id-anchored ancestor down to `el`.
function cssPath(win, el) {
    const parts = [];
    let node = el;
    const stop = el.ownerDocument ? el.ownerDocument.body : null;
    while (node && node.nodeType === 1 && node !== stop) {
        let seg = node.tagName.toLowerCase();
        if (node.id) {
            seg += '#' + cssEscapeIdent(win, node.id);
            parts.unshift(seg);
            return parts.join(' > '); // an id anchors the path — stop climbing
        }
        seg += ':nth-of-type(' + nthOfType(node) + ')';
        parts.unshift(seg);
        node = node.parentElement;
    }
    return parts.join(' > ');
}

const TEXTY_TAGS = /^(button|a|option|label|summary)$/;

// Capture a locator descriptor from a live element.
function describe(win, el) {
    const d = { tag: (el.tagName || '').toLowerCase() };
    const testid = getAttr(el, 'data-testid') || getAttr(el, 'data-test') || getAttr(el, 'data-cy');
    if (testid) d.testid = testid;
    const aria = getAttr(el, 'aria-label');
    if (aria) d.ariaLabel = aria;
    if (el.id) d.id = el.id;
    const name = getAttr(el, 'name');
    if (name) d.name = name;
    const type = getAttr(el, 'type');
    if (type) d.type = type;
    const role = getAttr(el, 'role');
    if (role) d.role = role;
    if (TEXTY_TAGS.test(d.tag) || d.type === 'submit' || d.type === 'button' || d.role === 'button') {
        const txt = normText(el);
        if (txt) d.text = txt;
    }
    d.css = cssPath(win, el);
    return d;
}

function firstMatch(doc, selector) {
    try {
        return doc.querySelector(selector);
    } catch (_) {
        return null;
    }
}

function allMatch(doc, selector) {
    try {
        return Array.prototype.slice.call(doc.querySelectorAll(selector));
    } catch (_) {
        return [];
    }
}

// Resolve a descriptor back to a live element in `doc`, trying each strategy in
// priority order. Returns the element or null.
function resolve(doc, d) {
    if (!d) return null;

    if (d.testid) {
        const v = attrValue(d.testid);
        const el = firstMatch(doc, `[data-testid="${v}"],[data-test="${v}"],[data-cy="${v}"]`);
        if (el) return el;
    }
    if (d.ariaLabel) {
        const v = attrValue(d.ariaLabel);
        const el = firstMatch(doc, `${d.tag || '*'}[aria-label="${v}"]`) || firstMatch(doc, `[aria-label="${v}"]`);
        if (el) return el;
    }
    if (d.id) {
        const el = doc.getElementById ? doc.getElementById(d.id) : firstMatch(doc, '#' + d.id);
        if (el) return el;
    }
    if (d.name) {
        const v = attrValue(d.name);
        const typed = d.type ? `[type="${attrValue(d.type)}"]` : '';
        const el = firstMatch(doc, `${d.tag || '*'}[name="${v}"]${typed}`) || firstMatch(doc, `[name="${v}"]`);
        if (el) return el;
    }
    if (d.text) {
        const tag = d.tag && d.tag !== '*'
            ? d.tag
            : 'button,a,[role=button],input[type=submit],input[type=button],option,summary,label';
        const candidates = allMatch(doc, tag);
        const exact = candidates.find((el) => normText(el) === d.text);
        if (exact) return exact;
        const partial = candidates.find((el) => normText(el).indexOf(d.text) >= 0);
        if (partial) return partial;
    }
    if (d.css) {
        const el = firstMatch(doc, d.css);
        if (el) return el;
    }
    return null;
}

// Cheap equality used by the recorder to collapse consecutive edits on the
// same field into a single step.
function sameTarget(a, b) {
    if (!a || !b) return false;
    if (a.testid || b.testid) return a.testid === b.testid;
    if (a.id || b.id) return a.id === b.id;
    if (a.name || b.name) return a.name === b.name && a.type === b.type;
    return a.css === b.css;
}

module.exports = { describe, resolve, sameTarget, normText, _internal: { cssPath, nthOfType, attrValue } };
